'use strict';

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { ENABLE_PIPELINE_STATUS } from '@/config/env.config';
import { usePermissions } from '@/hooks/permissions.hook';
import * as permissions from '@/security/permissionTypes';
import { usePipelineStatusActivityQuery, usePipelineStatusGraphsQuery } from '@/redux/services/portal/pipelineStatus.api';
import { composePipelineStatusLiveCatalog } from '@/features/pipelineStatus/composePipelineStatusLiveGraph';
import { ACTIVITY_STRIP_MAX_AGE_MS, activityProjectHomePath, processActivityStripRows, formatActivityStripLine } from './activityStripUtils';
import './ActivityStrip.scss';

const POLL_MS = 60000;
const FETCH_MINUTES = ACTIVITY_STRIP_MAX_AGE_MS / (60 * 1000);
const SECONDS_PER_ITEM = 5;
const MIN_DURATION_SEC = 20;
const REDUCE_MOTION_MQ = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion() {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return false;
		}
		return window.matchMedia(REDUCE_MOTION_MQ).matches;
	});

	useLayoutEffect(() => {
		if (typeof window.matchMedia !== 'function') return undefined;
		const mq = window.matchMedia(REDUCE_MOTION_MQ);
		const onChange = () => setPrefersReducedMotion(Boolean(mq.matches));
		onChange();
		if (typeof mq.addEventListener === 'function') {
			mq.addEventListener('change', onChange);
			return () => mq.removeEventListener('change', onChange);
		}
		mq.addListener(onChange);
		return () => mq.removeListener(onChange);
	}, []);

	return prefersReducedMotion;
}

function rowKey(row, idx, prefix) {
	const id = row.id != null ? String(row.id) : `activity-${idx}-${row.subjectId}-${row.stage}`;
	return prefix ? `${prefix}-${id}` : id;
}

function itemToneClass(row) {
	if (row?.activityTone === 'failed' || row?.activityTone === 'ready') {
		return ` activity-strip__item--${row.activityTone}`;
	}
	return '';
}

function TickerBookmark({ label }) {
	return <span className="activity-strip__bookmark" title={label} />;
}

/**
 * One half of the looping marquee. Two identical segments sit side-by-side so
 * `translateX(-50%)` is a seamless wrap. Each segment is at least as wide as
 * the ticker viewport, so a short list does not show both copies at once.
 * Orange bookmarks mark the start and end of the item list so the restart is
 * visible.
 */
function TickerSegment({ items, definitions, keyPrefix, minWidthPx, getItemHref, tabbable }) {
	return (
		<div
			className="activity-strip__segment"
			style={minWidthPx > 0 ? { minWidth: minWidthPx } : undefined}
			aria-hidden={tabbable ? undefined : true}
		>
			<TickerBookmark label="Start of activity list" />
			{items.map((row, idx) => {
				const line = formatActivityStripLine(row, definitions);
				const href = getItemHref ? getItemHref(row) : null;
				const className = `activity-strip__item${itemToneClass(row)}${href ? ' activity-strip__item--link' : ''}`;
				if (href) {
					return (
						<Link
							key={rowKey(row, idx, keyPrefix)}
							className={className}
							to={href}
							title={`${line}. Open project home`}
							tabIndex={tabbable ? undefined : -1}
						>
							{line}
						</Link>
					);
				}
				return (
					<span
						key={rowKey(row, idx, keyPrefix)}
						className={className}
						title={line}
					>
						{line}
					</span>
				);
			})}
			<TickerBookmark label="End of activity list" />
		</div>
	);
}

/**
 * Presentational looping ticker. Used by the live header strip and the
 * `/home/pipeline-tracker` mock preview (fixture rows, no API).
 *
 * @param {{
 *   items: Array<Record<string, unknown>>;
 *   definitions?: import('../../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphDefinition[];
 *   isFetching?: boolean;
 *   showLoadingBar?: boolean;
 *   ariaLabel?: string;
 *   getItemHref?: (row: Record<string, unknown>) => string | null;
 * }} props
 */
export function ActivityStripView({
	items,
	definitions = [],
	isFetching = false,
	showLoadingBar = false,
	ariaLabel = 'Recent pipeline activity',
	getItemHref,
}) {
	const prefersReducedMotion = usePrefersReducedMotion();
	const viewportRef = useRef(null);
	const [segmentMinWidthPx, setSegmentMinWidthPx] = useState(0);

	useLayoutEffect(() => {
		const el = viewportRef.current;
		if (!el) return undefined;

		const apply = () => {
			const width = el.clientWidth;
			setSegmentMinWidthPx((prev) => (prev === width ? prev : width));
		};
		apply();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(apply);
		observer.observe(el);
		return () => observer.disconnect();
	}, [items.length]);

	if (!showLoadingBar && items.length === 0) {
		return null;
	}

	const durationSec = Math.max(MIN_DURATION_SEC, items.length * SECONDS_PER_ITEM);

	return (
		<div
			className="activity-strip"
			role="region"
			aria-label={ariaLabel}
			aria-description={
				prefersReducedMotion
					? `Static list. Scroll horizontally to see all activity.${getItemHref ? ' Choose an item to open that project.' : ''}`
					: `Scrolling ticker. Hover to pause.${getItemHref ? ' Choose an item to open that project.' : ''}`
			}
		>
			{showLoadingBar && (
				<LinearProgress
					className="activity-strip__loading"
					variant="indeterminate"
					sx={{ height: 2 }}
				/>
			)}
			{items.length > 0 && (
				<>
					<div
						ref={viewportRef}
						className={`activity-strip__viewport${isFetching ? ' activity-strip__viewport--fetching' : ''}`}
						tabIndex={prefersReducedMotion ? 0 : undefined}
						data-testid="activity-strip-ticker"
					>
						<div
							className="activity-strip__track"
							style={{ '--activity-strip-duration': `${durationSec}s` }}
						>
							<TickerSegment items={items} definitions={definitions} keyPrefix="a" minWidthPx={segmentMinWidthPx} getItemHref={getItemHref} tabbable />
							<TickerSegment items={items} definitions={definitions} keyPrefix="b" minWidthPx={segmentMinWidthPx} getItemHref={getItemHref} tabbable={false} />
						</div>
					</div>
				</>
			)}
		</div>
	);
}

/**
 * Global pipeline activity strip (admin-only). Data from GET /api/v1/admin/pipeline-status/activity.
 * SignalR PipelineActivityChanged refetches that query. Polling is the fallback when the hub is down.
 * Requires canViewPipelineStatus. Renders as a CNBC-style looping ticker (no scrollbar).
 * With prefers-reduced-motion, the ticker is a static horizontally scrollable row.
 * Hidden on `/home/pipeline-tracker` so the mock showcase ticker is the only tape on that page.
 */
function isPipelineTrackerShowcasePath(pathname) {
	return /\/pipeline-tracker\/?$/.test(String(pathname ?? ''));
}

export function ActivityStrip() {
	const { checkUserHasPermissions } = usePermissions();
	const canViewPipelineStatus = checkUserHasPermissions(permissions.CAN_VIEW_PIPELINE_STATUS);
	const onShowcaseRoute =
		typeof window !== 'undefined' && isPipelineTrackerShowcasePath(window.location.pathname);

	const canShowActivityStrip = ENABLE_PIPELINE_STATUS && canViewPipelineStatus && !onShowcaseRoute;

	const { data, isLoading, isFetching, error } = usePipelineStatusActivityQuery(
		{ minutes: FETCH_MINUTES },
		{
			skip: !canShowActivityStrip,
			pollingInterval: POLL_MS,
			refetchOnMountOrArgChange: true,
		}
	);
	const graphsQuery = usePipelineStatusGraphsQuery(undefined, {
		skip: !canShowActivityStrip,
	});

	const { definitions, catalogError } = useMemo(() => {
		if (graphsQuery.error) {
			return {
				definitions: [],
				catalogError:
					'[pipelineStatus] PortalApi graph catalog request failed. Deploy PortalApi with GET /admin/pipeline-status/graphs before this Aperture.',
			};
		}
		if (!graphsQuery.data) {
			return { definitions: [], catalogError: null };
		}
		try {
			return { definitions: composePipelineStatusLiveCatalog(graphsQuery.data), catalogError: null };
		} catch (err) {
			return {
				definitions: [],
				catalogError: err instanceof Error ? err.message : String(err),
			};
		}
	}, [graphsQuery.data, graphsQuery.error]);

	const items = useMemo(() => {
		const rows = Array.isArray(data) ? data : [];
		return processActivityStripRows(rows, definitions);
	}, [data, definitions]);

	if (!canShowActivityStrip) {
		return null;
	}

	if (catalogError) {
		return (
			<div className="activity-strip" data-testid="activity-strip-catalog-error" title={catalogError}>
				<span className="activity-strip__item activity-strip__item--failed">{catalogError}</span>
			</div>
		);
	}

	if (error) {
		return null;
	}

	const catalogPending = !graphsQuery.data && !graphsQuery.error;
	const showLoadingBar = ((isLoading || isFetching) && !data) || catalogPending;
	const showEmpty = !isLoading && !data?.length;
	const hideCompletely = showEmpty || items.length === 0;

	if (hideCompletely && !showLoadingBar) {
		return null;
	}

	return (
		<ActivityStripView
			items={items}
			definitions={definitions}
			isFetching={isFetching}
			showLoadingBar={showLoadingBar}
			getItemHref={activityProjectHomePath}
		/>
	);
}

export default ActivityStrip;
